import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ListRequest,
  useSamplesDeliveryListQuery,
} from "@/services/backend/samples/api/delivery-api";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import t from "@/lang/lang";
import { LaboratoryName } from "@/services/backend/samples/components/laboratory-name";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { useState } from "react";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { LaboratoryFilter } from "./laboratory-filter";
import { CancelDeliveryDialog } from "./cancel-delivery-dialog";
import { DeliveryStatusDialog } from "./delivery-status-dialog";
import { EditDeliveryDialog } from "./edit-delivery-dialog";
import { Button } from "@/shared/components/ui/button";
import { Pencil } from "lucide-react";

export function DeliveriesCard({
  initialRequest,
}: {
  initialRequest?: ListRequest;
}) {
  const [request, setRequest] = useState<ListRequest>(initialRequest ?? {});

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Lieferungen")}</CardTitle>
        <CardAction>
          <DeliveriesTableFilters
            request={request}
            onRequestChange={setRequest}
          />
          <DeliveriesTableActions />
        </CardAction>
      </CardHeader>
      <CardContent>
        <DeliveriesTable request={request} onRequestChange={setRequest} />
      </CardContent>
    </Card>
  );
}

export function DeliveriesTable({
  request,
  onRequestChange,
  hidePagination = false,
}: {
  request: ListRequest;
  onRequestChange: (r: ListRequest) => void;
  hidePagination?: boolean;
}) {
  const { data, isLoading, error } = useSamplesDeliveryListQuery(request);

  return (
    <div className="grid gap-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Tracking ID")}</TableHead>
            <TableHead>{t("Labor")}</TableHead>
            <TableHead>{t("Erstellt am")}</TableHead>
            <TableHead>{t("Gesendet am")}</TableHead>
            <TableHead>{t("Labor Status")}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && <TableLoadingRow colSpan={5} />}
          {error && <TableRtkErrorRow error={error} colSpan={5} />}
          {data && data.items.length === 0 && <TableEmptyRow colSpan={6} />}
          {data &&
            data.items.map((delivery) => (
              <TableRow key={delivery.id}>
                <TableCell>{delivery.trackingId}</TableCell>
                <TableCell>
                  {delivery.labId && <LaboratoryName id={delivery.labId} />}
                </TableCell>
                <TableCell>{formatDateTime(delivery.createdAt)}</TableCell>
                <TableCell>{formatDateTime(delivery.sentAt)}</TableCell>
                <TableCell className="py-0">
                  <DeliveryStatusDialog delivery={delivery} />
                </TableCell>
                <TableCell className="py-0 text-right">
                  {delivery.cancelled ? (
                    t("Lieferung storniert")
                  ) : (
                    <div className="flex justify-end gap-2">
                      <EditDeliveryDialog
                        delivery={delivery}
                        trigger={
                          <Button size="sm" variant="outline">
                            <Pencil />
                          </Button>
                        }
                      />
                      <CancelDeliveryDialog delivery={delivery} />
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {!hidePagination && data && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={data.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

export function DeliveriesTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (r: ListRequest) => void;
}) {
  return (
    <div className="flex gap-2">
      <LaboratoryFilter
        filter={request.laboratory}
        onFilterChange={(laboratory) =>
          onRequestChange({ ...request, laboratory })
        }
      />
    </div>
  );
}

export function DeliveriesTableActions() {
  return <div></div>;
}
